import { put, call } from 'redux-saga/effects'
import {
  updateColumns,
  syncSelectedCondition,
  changeKeywords,
  syncSortOrder,
  setTags,
  addTagsToKeywords,
  setInitStatusCondition,
  setInitContactPersonCondition,
  setPage,
} from '../actions'
import queryString from 'query-string'
import {
  COLUMN_NAMES_WITH_STATUS_EARLY_ACCESS,
  ColumnName,
  ColumnState,
  initialColumns,
} from '../reducers/column'
import { buildCondintion, buildStatusCondition } from '../sagas'
import { paramsOf } from '../libs/params'
import { validateConditions } from './validate'
import { isSortingOrder, isSortableField } from '../constants/orders'
import {
  setInitFirstInterviewDate,
  setInitMyLabelCondition,
  setInitSourcingRoutesCondition,
  setInitStatusUpdatedAt,
} from '../actions/statusCondition'
import {
  FirstInterviewDateCondition,
  SourcingRouteId,
  StatusUpdatedCondition,
} from '../reducers/condition'
import {
  ConditionState,
  ContactPeopleCondition,
  StatusCondition,
  StatusConditionState,
} from '../reducers/condition'
import { MAX_PAGE } from '../components/pagination'

function parseQuery(locationSearch: string) {
  return queryString.parse(locationSearch, {
    arrayFormat: 'index',
  })
}

function* setKeywordCondition(keywords: string | string[] | null | undefined) {
  if (typeof keywords === 'string') {
    yield put(changeKeywords(keywords))
  }
}

// 関数にしてやらないと、yieldが使えなくてエラーになる・・・
function pageNumber(page: string) {
  let pageNumber = Number(page)
  if (pageNumber > MAX_PAGE) {
    pageNumber = 1
  }
  return pageNumber
}

function* setPageCondition(page: string | string[] | null | undefined) {
  if (typeof page === 'string') {
    yield put(setPage(pageNumber(page)))
  }
}

function* setTagsCondition(tags: string | string[] | null | undefined) {
  if (typeof tags === 'string') {
    yield put(setTags(tags))
  }
}

function* addTagsToKeywordsCondition(
  tags: string | string[] | null | undefined
) {
  if (typeof tags === 'string') {
    yield put(addTagsToKeywords(tags))
  }
}

//TODO 一旦引数の型指定でanyにしたが、できればanyはやめたい
function* setPagerCondtion({ sort, order }: { sort: any; order: any }) {
  const pager = {
    offset: 0, // offset保存したい要件あるだろうか？
    // offset: isNaN(Number(offset)) ? undefined : Number(offset),
    order: isSortingOrder(order) ? order : undefined,
    sort: isSortableField(sort) ? sort : undefined,
  }
  yield put(syncSortOrder(pager))
}

function* setSelectedCondtion(selected: {
  [key: string]: string | string[] | null | undefined
}) {
  const condition = paramsOf(selected)
  yield put(syncSelectedCondition(condition))
}

const paramsToColumns = (
  fields: string | string[] | null | undefined
): ColumnName[] => {
  if (!fields) {
    return []
  }
  //TODO fieldsの型を指定したいので暫定的な対応
  if (Array.isArray(fields)) {
    return []
  }
  return fields.split(',').filter((col): col is ColumnName => {
    return Object.keys(COLUMN_NAMES_WITH_STATUS_EARLY_ACCESS).includes(col)
  })
}

function* setColumns(fields: string | string[] | null | undefined) {
  const columns = paramsToColumns(fields)
  if (columns.length > 0) {
    yield put(updateColumns(columns))
  }
}

function* setStatus(fields: string | string[] | null | undefined) {
  if (!fields || Array.isArray(fields)) {
    return
  }

  const statusConditions: StatusCondition[] = fields.split(',').map(
    (s) =>
      <StatusCondition>{
        id: s,
      }
  )

  if (statusConditions.length < 1) {
    return
  }

  yield put(setInitStatusCondition(statusConditions))
}

function* setStatusUpdatedAt(
  from: string | string[] | null | undefined,
  to: string | string[] | null | undefined
) {
  if ((!from && !to) || Array.isArray(from) || Array.isArray(to)) {
    return
  }

  const statusUpdatedCondition: StatusUpdatedCondition = {
    from: from?.split('T')[0] ?? null,
    to: to?.split('T')[0] ?? null,
  }

  yield put(setInitStatusUpdatedAt(statusUpdatedCondition))
}

function* setFirstInterviewDate(
  from: string | string[] | null | undefined,
  to: string | string[] | null | undefined
) {
  if ((!from && !to) || Array.isArray(from) || Array.isArray(to)) {
    return
  }

  const firstInterviewDateCondition: FirstInterviewDateCondition = {
    from: from?.split('T')[0] ?? null,
    to: to?.split('T')[0] ?? null,
  }

  yield put(setInitFirstInterviewDate(firstInterviewDateCondition))
}

function* setContactPeople(fields: string | string[] | null | undefined) {
  if (!fields || Array.isArray(fields)) {
    return
  }

  const contactPersonConditions: ContactPeopleCondition[] = fields
    .split(',')
    .map(
      (s) =>
        <ContactPeopleCondition>{
          id: Number(s),
        }
    )

  if (contactPersonConditions.length < 1) {
    return
  }
  yield put(setInitContactPersonCondition(contactPersonConditions))
}

function* setSourcingRoutes(fields: string | string[] | null | undefined) {
  if (!fields || Array.isArray(fields)) return
  const condition: SourcingRouteId[] = fields
    .split(',')
    .map((it) => ({ id: parseInt(it) }))
  yield put(setInitSourcingRoutesCondition(condition))
}

function* setMyLabels(fields: string | string[] | null | undefined) {
  if (!fields) return
  const myLabels = Array.isArray(fields) ? fields[0] : fields
  const condition = myLabels.split(',').map((it) => parseInt(it, 10))
  yield put(setInitMyLabelCondition(condition))
}

const isDefaultFields = (fields: string) =>
  fields.trim() === initialColumns.join(',').trim()

// limit,offsetは利用しない（入力側でも防ぐが、下手に要素としても与えたくない）
// fieldsの値は変化がなければ共有URLから消してしまう（ここは要検討かも？）
export const buildShareUrl = (
  condition: ConditionState,
  column: ColumnState,
  statusCondition: StatusConditionState
) => {
  const { fields, limit, offset, ...rest } = buildCondintion(condition, column)
  const fieldNormalized = isDefaultFields(fields)
    ? {}
    : {
        fields: fields,
      }
  const q = {
    ...rest,
    ...fieldNormalized,
  }
  const statusQuery = buildStatusCondition(statusCondition)
  let statusQueryString = ''
  if (statusQuery) {
    const sq = queryString.stringify(statusQuery)
    statusQueryString = queryString.stringify(q) && sq !== '' ? `&${sq}` : sq
  }
  let url = ''
  if (typeof window !== 'undefined') {
    // 画面描画が終わってからURLを取得する
    url = window.location.origin + window.location.pathname
  }
  return `${url}?${queryString.stringify(q)}${statusQueryString}`
}

export function* setCondition() {
  const locationSearch = location.search
  const {
    /*limit, offset,*/
    sort,
    keywords,
    tags,
    order,
    fields,
    status,
    contactPerson,
    contactPeople,
    page,
    statusUpdatedFrom,
    statusUpdatedTo,
    firstInterviewDateFrom,
    firstInterviewDateTo,
    sourcingRouteIds,
    myLabelIds,
    ...selected
  } = parseQuery(locationSearch)

  yield call(setTagsCondition, tags)
  yield call(setKeywordCondition, keywords)
  yield call(addTagsToKeywordsCondition, tags)
  yield call(setPageCondition, page)
  yield call(setSelectedCondtion, selected)
  yield call(setPagerCondtion, { sort, order })
  yield call(setColumns, fields)
  yield call(setStatus, status)
  yield call(setStatusUpdatedAt, statusUpdatedFrom, statusUpdatedTo)
  yield call(
    setFirstInterviewDate,
    firstInterviewDateFrom,
    firstInterviewDateTo
  )
  // 過去の名前との互換性を保つために、どちらも受け付ける
  const contactPersonIds = contactPeople ?? contactPerson
  yield call(setContactPeople, contactPersonIds)
  yield call(setSourcingRoutes, sourcingRouteIds)
  yield call(setMyLabels, myLabelIds)
  yield call(validateConditions)
}
